/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

export const userMediaSlice = createSlice({
  name: 'userMedia',
  initialState: {
    stream: false,
    streams: [],
    hidDevice: null,
    hidDevices: [],
    yealinkDevice: null,
    yealinkDevices: [],
    headsetType: 'none', // 'jabra', 'yealink', 'none'
    inputDevice: null,
    inputDevices: [],
    outputDevice: null,
    outputDeviceRinger: null,
    outputDevices: [],
    hasPermission: false,
    pwaPrompt: null,
  },
  reducers: {
    setStream: (state, action) => {
      state.hasStream = action.payload;
    },
    setStreams: (state, action) => {
      state.streams = action.payload;
    },
    setHidDevice: (state, action) => {
      state.hidDevice = action.payload;
    },
    setHidDevices: (state, action) => {
      state.hidDevices = action.payload;
    },
    setYealinkDevice: (state, action) => {
      state.yealinkDevice = action.payload;
    },
    setYealinkDevices: (state, action) => {
      state.yealinkDevices = action.payload;
    },
    setHeadsetType: (state, action) => {
      state.headsetType = action.payload;
    },
    setInputDevice: (state, action) => {
      state.inputDevice = action.payload;
    },
    setInputDevices: (state, action) => {
      state.inputDevices = action.payload;
    },
    setOutputDevice: (state, action) => {
      state.outputDevice = action.payload;
    },
    setOutputDeviceRinger: (state, action) => {
      state.outputDeviceRinger = action.payload;
    },
    setOutputDevices: (state, action) => {
      state.outputDevices = action.payload;
    },
    setHasPermission: (state, action) => {
      state.hasPermission = action.payload;
    },
    setPWAPrompt: (state, action) => {
      state.pwaPrompt = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setStream,
  setStreams,
  setHidDevice,
  setHidDevices,
  setYealinkDevice,
  setYealinkDevices,
  setHeadsetType,
  setInputDevice,
  setInputDevices,
  setOutputDevice,
  setOutputDeviceRinger,
  setOutputDevices,
  setHasPermission,
  setPWAPrompt,
} = userMediaSlice.actions;

// Selectors
export const selectStream = (state) => state.userMedia.stream;
export const selectStreams = (state) => state.userMedia.streams;
export const selectHidDevice = (state) => state.userMedia.hidDevice;
export const selectHidDevices = (state) => state.userMedia.hidDevices;
export const selectYealinkDevice = (state) => state.userMedia.yealinkDevice;
export const selectYealinkDevices = (state) => state.userMedia.yealinkDevices;
export const selectHeadsetType = (state) => state.userMedia.headsetType;
export const selectInputDevice = (state) => state.userMedia.inputDevice;
export const selectInputDevices = (state) => state.userMedia.inputDevices;
export const selectOutputDevice = (state) => state.userMedia.outputDevice;
export const selectOutputDeviceRinger = (state) => state.userMedia.outputDeviceRinger;
export const selectOutputDevices = (state) => state.userMedia.outputDevices;
export const selectHasPermission = (state) => state.userMedia.hasPermission;
export const selectPWAPrompt = (state) => state.userMedia.pwaPrompt;

// Combined selectors for unified headset management
export const selectActiveHeadsetDevice = (state) => {
  const headsetType = selectHeadsetType(state);
  switch (headsetType) {
    case 'jabra':
      return selectHidDevice(state);
    case 'yealink':
      return selectYealinkDevice(state);
    default:
      return selectHidDevice(state);
  }
};

export const selectAllHeadsetDevices = (state) => {
  const jabra = selectHidDevices(state) || [];
  const yealink = selectYealinkDevices(state) || [];

  // Create combined list with type annotations
  const jabraWithType = jabra.map((device) => ({ ...device, type: 'jabra' }));
  const yealinkWithType = yealink.map((device) => ({ ...device, type: 'yealink' }));
  const allDevices = [...jabraWithType, ...yealinkWithType];

  // Deduplicate based on vendor/product ID, prioritizing Yealink
  const deviceMap = new Map();

  allDevices.forEach((device) => {
    // Generate unique key based on vendor/product ID
    const vendorId = device.vendorId || device.device?.vendorId;
    const productId = device.productId || device.device?.productId;
    const deviceKey = `${vendorId}-${productId}`;

    if (!deviceMap.has(deviceKey)) {
      deviceMap.set(deviceKey, device);
    } else {
      // If duplicate exists, prioritize Yealink over Jabra
      const existing = deviceMap.get(deviceKey);
      if (device.type === 'yealink' && existing.type === 'jabra') {
        deviceMap.set(deviceKey, device);
      }
    }
  });

  return Array.from(deviceMap.values());
};

export default userMediaSlice.reducer;
